import { Loading } from "@mnk/frontend/components/Loading";
import { Drawer } from "@mnk/frontend/components/domain/drawer";
import { NoDrawing } from "@mnk/frontend/components/domain/drawer/NoDrawing";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { Drawing } from "@mnk/shared/models/drawing";
import type { Quantity } from "@mnk/shared/models/quantity";
import { useEffect, useState } from "react";
import { LeftSideBar } from "./LeftSideBar";
import { QuantityModal } from "./QuantityModal";
import { SelectDrawingModal } from "./SelectDrawingModal";

interface Props {
  projectId: string;
  officeName: string;
}

export const ProjectDetailLayout = ({ projectId, officeName }: Props) => {
  const [quantity, setQuantity] = useState<Quantity | null>(null);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const { data: drawings, isLoading, refetch } = trpc.getDrawingByProjectId.useQuery({ projectId });

  const [selectedDrawing, setSelectedDrawing] = useState<Drawing | null>(null);
  const [showSelectDrawingModal, setShowSelectDrawingModal] = useState(false);

  useEffect(() => {
    if (!drawings) return;

    if (drawings.length === 0) {
      setSelectedDrawing(null);
    } else {
      const drawingExists = drawings.some((drawing: Drawing) => drawing.id === selectedDrawing?.id);
      if (!drawingExists) {
        setSelectedDrawing(drawings[0]);
      }
    }
  }, [drawings, selectedDrawing]);

  if (isLoading) return <Loading />;

  return (
    <main className="flex flex-row h-full bg-gray-100 gap-4 p-4">
      <LeftSideBar
        drawings={drawings ?? []}
        selectedDrawing={selectedDrawing}
        setShowSelectDrawingModal={setShowSelectDrawingModal}
        setSelectedDrawing={setSelectedDrawing}
        reloadDrawings={async () => {
          await refetch();
          return;
        }}
        projectId={projectId}
        setShowQuantityModal={setShowQuantityModal}
        setQuantity={setQuantity}
      />
      {selectedDrawing && quantity ? (
        <Drawer projectId={projectId} drawing={selectedDrawing} quantityId={quantity.id} officeName={officeName} />
      ) : (
        <NoDrawing />
      )}
      {showSelectDrawingModal && (
        <SelectDrawingModal
          onClose={() => setShowSelectDrawingModal(false)}
          drawings={drawings ?? []}
          projectId={projectId}
          reloadDrawings={async () => {
            await refetch();
            return;
          }}
        />
      )}
      {showQuantityModal && quantity && (
        <QuantityModal onClose={() => setShowQuantityModal(false)} quantityId={quantity.id} projectId={projectId} />
      )}
    </main>
  );
};
